import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

// Returns what the OS/browser prefers
const getSystemTheme = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

export const ThemeProvider = ({ children }) => {
  // 'light' | 'dark' | 'system'
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'system';
    const saved = localStorage.getItem('themeMode');
    if (saved === 'dark' || saved === 'light' || saved === 'system') return saved;
    // Default: system — no saved preference means follow the OS
    return 'system';
  });

  // Derive the actual applied theme from mode
  const resolvedTheme = mode === 'system' ? getSystemTheme() : mode;

  // Apply/remove "dark" class on <html> whenever the resolved theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedTheme]);

  // Persist the MODE (not the resolved theme) — only when user has a preference
  useEffect(() => {
    if (mode === 'system') {
      // System = no manual override, so clear any saved preference
      localStorage.removeItem('themeMode');
    } else {
      localStorage.setItem('themeMode', mode);
    }
  }, [mode]);

  // Listen for OS preference changes — only affects UI when mode is 'system'
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Force a re-render so resolvedTheme recalculates if mode === 'system'
      if (mode === 'system') {
        // Trigger re-render by toggling a dummy state update
        setMode('system');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode]);

  // Toggle between dark and light (skips system)
  const toggleTheme = () => {
    setMode((prev) => {
      const current = prev === 'system' ? getSystemTheme() : prev;
      return current === 'dark' ? 'light' : 'dark';
    });
  };

  const setLightTheme = () => setMode('light');
  const setDarkTheme = () => setMode('dark');
  const setSystemTheme = () => setMode('system');

  // theme exposed to consumers is the resolved value ('light' | 'dark')
  const theme = resolvedTheme;

  return (
    <ThemeContext.Provider
      value={{ theme, mode, toggleTheme, setLightTheme, setDarkTheme, setSystemTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};
